import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';
import { ArrowLeft, Banknote, Landmark } from 'lucide-react';
import {
  api,
  ApiError,
  type FxQuoteDTO,
  type OnRampAttemptWithInstructionsDTO,
  type PublicInvoiceDTO,
} from '../lib/api.ts';
import { BankingInstructions } from '../components/BankingInstructions.tsx';

// On-ramp flow:
//   1. Buyer picks a target (sending) currency + a payment method.
//   2. We POST /public/invoices/:publicId/fx-quote to lock a 10-min FX rate.
//   3. Buyer reviews the locked quote, then clicks "Confirm".
//   4. We POST /public/invoices/:publicId/on-ramp with paymentMethod + fxQuoteId.
//   5. The response carries banking instructions — we render them in a
//      copyable layout and link the buyer to /tracking.

// Keep the method enum aligned with the OnRampMethod schema in shared-types.
// Copy is gated to the methods we currently surface on the buyer portal —
// `card` is intentionally absent until we offer card-funded on-ramps.
type Method = 'ach' | 'wire' | 'sepa';
const METHODS: ReadonlyArray<Method> = ['ach', 'wire', 'sepa'];

// Dropdown of sending currencies. Kept short on purpose; the FX quote
// endpoint will reject anything BlindPay does not support and we surface
// the error inline.
const TARGET_CURRENCIES: ReadonlyArray<string> = ['USD', 'EUR', 'GBP'];

function OnRampPage() {
  const { publicId } = Route.useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const invoice = useQuery<PublicInvoiceDTO, ApiError>({
    queryKey: ['public-invoice', publicId],
    queryFn: () => api.getPublicInvoice(publicId),
  });

  const [method, setMethod] = useState<Method>('ach');
  const [targetCurrency, setTargetCurrency] = useState<string>('USD');
  const [quote, setQuote] = useState<FxQuoteDTO | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  // Tick once per second so the "expires in" relative time stays current.
  // Cheap; mounted only on this page.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const lockQuote = useMutation<FxQuoteDTO, ApiError, string>({
    mutationFn: (currency) => api.createFxQuote(publicId, currency),
    onSuccess: (data) => setQuote(data),
  });

  const confirm = useMutation<OnRampAttemptWithInstructionsDTO, ApiError, void>({
    mutationFn: () => {
      if (!quote) throw new Error('No active quote');
      return api.initiateOnRamp(publicId, {
        paymentMethod: method,
        fxQuoteId: quote.id,
      });
    },
    onSuccess: () => {
      // Initiating the on-ramp transitions the invoice fx_quoted -> on_ramp_pending
      // server-side. Patch the cached public-invoice status so when the buyer
      // taps "Track this payment" the tracking page doesn't bounce them straight
      // back here: its auto-advance redirect fires on isOnRampReady states
      // (buyer_kyc_approved/fx_quoted/fx_expired) and would otherwise act on the
      // stale fx_quoted still in cache. Invalidate to reconcile with the server.
      queryClient.setQueryData<PublicInvoiceDTO>(['public-invoice', publicId], (prev) =>
        prev ? { ...prev, status: 'on_ramp_pending' } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: ['public-invoice', publicId] });
    },
  });

  // If we already initiated and the response lacks banking instructions,
  // bounce to /tracking so the buyer can wait on email instead. When
  // instructions arrive we render them inline.
  useEffect(() => {
    if (confirm.isSuccess && !confirm.data?.bankingInstructions) {
      void navigate({
        to: '/r/$publicId/tracking',
        params: { publicId },
        replace: true,
      });
    }
  }, [confirm.isSuccess, confirm.data, navigate, publicId]);

  const expiresAt = useMemo(() => (quote ? new Date(quote.expiresAt).getTime() : null), [quote]);
  const expired = expiresAt !== null && expiresAt <= now;

  if (confirm.isSuccess && confirm.data?.bankingInstructions) {
    return <Instructions data={confirm.data} publicId={publicId} />;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Link
        to="/r/$publicId"
        params={{ publicId }}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-navy-600)] hover:text-[var(--color-navy-900)]"
      >
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        <FormattedMessage id="tracking.cta.back" />
      </Link>

      <div className="mt-4 rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--color-navy-900)]">
          <Banknote size={20} strokeWidth={1.5} aria-hidden="true" />
          <FormattedMessage id="onramp.heading" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-navy-600)]">
          <FormattedMessage id="onramp.subheading" />
        </p>

        {invoice.data && (
          <div className="mt-4 rounded-md bg-[var(--color-beige-50)] px-4 py-3 text-sm text-[var(--color-navy-700)]">
            <Landmark
              size={14}
              strokeWidth={1.5}
              aria-hidden="true"
              className="mr-2 inline-block"
            />
            {intl.formatMessage({ id: 'invoice.title' }, { seller: invoice.data.seller.name })}
            <span className="ml-2 font-mono">
              {intl.formatNumber(Number(invoice.data.amount), {
                style: 'currency',
                currency: invoice.data.currency,
              })}
            </span>
          </div>
        )}

        <fieldset className="mt-6">
          <legend className="text-sm font-medium text-[var(--color-navy-800)]">
            <FormattedMessage id="onramp.method.label" />
          </legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {METHODS.map((m) => (
              <label
                key={m}
                className={
                  'cursor-pointer rounded-md border px-3 py-2 text-sm ' +
                  (method === m
                    ? 'border-[var(--color-navy-800)] bg-[var(--color-navy-50)] text-[var(--color-navy-900)]'
                    : 'border-[var(--color-navy-200)] text-[var(--color-navy-700)] hover:border-[var(--color-navy-400)]')
                }
              >
                <input
                  type="radio"
                  name="method"
                  value={m}
                  checked={method === m}
                  onChange={() => setMethod(m)}
                  className="sr-only"
                />
                <FormattedMessage id={`onramp.method.${m}`} />
              </label>
            ))}
          </div>
        </fieldset>

        <label className="mt-6 block text-sm font-medium text-[var(--color-navy-800)]">
          <FormattedMessage id="onramp.targetCurrency.label" />
          <select
            value={targetCurrency}
            onChange={(e) => {
              setTargetCurrency(e.target.value);
              setQuote(null);
            }}
            className="mt-1 block w-full rounded-md border border-[var(--color-navy-200)] bg-white px-3 py-2 text-sm text-[var(--color-navy-900)] focus:outline-none focus:ring-2 focus:ring-[var(--color-navy-500)]"
          >
            {TARGET_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs font-normal text-[var(--color-navy-500)]">
            <FormattedMessage id="onramp.targetCurrency.helper" />
          </span>
        </label>

        {quote && !expired && expiresAt !== null && (
          <div className="mt-6 rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-3 text-sm text-[var(--color-navy-800)]">
            <FormattedMessage
              id="onramp.quote.locked"
              values={{
                expires: (
                  <FormattedRelativeTime
                    value={(expiresAt - now) / 1000}
                    numeric="auto"
                    updateIntervalInSeconds={1}
                  />
                ),
              }}
            />
            <div className="mt-1 font-mono text-xs text-[var(--color-navy-700)]">
              1 {quote.sourceCurrency} = {quote.rate} {quote.targetCurrency}
            </div>
          </div>
        )}

        {quote && expired && (
          <div className="mt-6 flex items-center justify-between rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]">
            <FormattedMessage id="onramp.quote.expired" />
            <button
              type="button"
              onClick={() => lockQuote.mutate(targetCurrency)}
              className="rounded-md border border-[var(--color-danger)] px-2 py-1 text-xs hover:bg-[var(--color-danger)]/20"
            >
              <FormattedMessage id="onramp.quote.refresh" />
            </button>
          </div>
        )}

        {(lockQuote.isError || confirm.isError) && (
          <p className="mt-4 text-sm text-[var(--color-danger)]" role="alert">
            <FormattedMessage id="onramp.error.generic" />
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {!quote || expired ? (
            <button
              type="button"
              onClick={() => lockQuote.mutate(targetCurrency)}
              disabled={lockQuote.isPending}
              className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FormattedMessage id="onramp.cta.lockQuote" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending}
              className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {confirm.isPending ? (
                <FormattedMessage id="onramp.cta.confirm.pending" />
              ) : (
                <FormattedMessage id="onramp.cta.confirm" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Instructions({
  data,
  publicId,
}: {
  data: OnRampAttemptWithInstructionsDTO;
  publicId: string;
}): React.ReactElement {
  const navigate = useNavigate();
  // Tapping "Track payment" simulates the buyer's deposit in test mode by
  // creating a BlindPay payin (shows on the dashboard + auto-completes); in live
  // mode the API treats it as a no-op. Either way we then go to /tracking.
  const track = useMutation({
    mutationFn: () => api.trackPayment(publicId),
    onSettled: () => {
      void navigate({ to: '/r/$publicId/tracking', params: { publicId } });
    },
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-[var(--color-navy-900)]">
          <FormattedMessage id="onramp.instructions.heading" />
        </h1>
        <p className="mt-1 mb-4 text-sm text-[var(--color-navy-600)]">
          <FormattedMessage id="onramp.instructions.subheading" />
        </p>

        <BankingInstructions
          instructions={data.bankingInstructions ?? null}
          fiatAmount={data.fiatAmount}
          fiatCurrency={data.fiatCurrency}
        />

        <div className="mt-8 flex justify-end">
          <button
            type="button"
            disabled={track.isPending}
            onClick={() => track.mutate()}
            className="rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:opacity-60"
          >
            <FormattedMessage id="onramp.instructions.continue" />
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/r/$publicId/on-ramp')({
  component: OnRampPage,
});
